import type { InsightsSavedView as BaseInsightsSavedView } from '@mend/types/insights-saved-view';
import type { Response } from '#types/common.ts';
import type { DatePresetKey } from '#utils/dates.ts';

import { z } from 'zod';

import { api } from '#services/http-clients.ts';
import { datePresets } from '#utils/dates.ts';

const datePresetKeys = Object.keys(datePresets) as [DatePresetKey, ...DatePresetKey[]];

const savedViewFiltersSchema = z
  .object({
    datePreset: z.enum(datePresetKeys).optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
    providerIds: z.array(z.coerce.string()).optional(),
    appointmentTypeIds: z.array(z.coerce.string()).optional(),
    addressIds: z.array(z.coerce.string()).optional(),
    bookingChannelIds: z.array(z.string()).optional(),
    baseAppointmentTypes: z.array(z.string()).optional(),
    assessmentTypeIds: z.array(z.coerce.string()).optional(),
    formsPriority: z.enum(['all', 'required', 'optional']).optional(),
  })
  .refine((data) => !data.datePreset || (!data.from && !data.to), {
    message: 'datePreset and from/to are mutually exclusive',
  })
  .refine((data) => !(data.from && !data.to) && !(data.to && !data.from), {
    message: 'from and to must both be present if either is set',
  });

export type SavedViewFilters = z.infer<typeof savedViewFiltersSchema>;

export interface InsightsSavedView extends Omit<BaseInsightsSavedView, 'filters'> {
  filters: SavedViewFilters;
}

export type SavedViewsListResponse = { views: InsightsSavedView[] };
type SavedViewResponse = { view: InsightsSavedView };

export const getSavedViews = (): Promise<SavedViewsListResponse> =>
  api.get<Response<{ views: BaseInsightsSavedView[] }>>('/insights/saved-views').then((res) => ({
    views: res.data.payload.views.reduce<InsightsSavedView[]>((acc, view) => {
      const result = savedViewFiltersSchema.safeParse(view.filters);
      if (result.success) {
        acc.push({ ...view, filters: result.data });
      } else {
        console.error(`[saved-views] Malformed filters for view ${view.id}:`, result.error);
      }
      return acc;
    }, []),
  }));

export const createSavedView = (params: {
  tabType: InsightsSavedView['tabType'];
  name: string;
  filters: SavedViewFilters;
}): Promise<SavedViewResponse> =>
  api
    .post<Response<SavedViewResponse>>('/insights/saved-views', params)
    .then((res) => res.data.payload);

export const updateSavedView = (
  id: number,
  params:
    | { name: string; filters?: SavedViewFilters }
    | { name?: string; filters: SavedViewFilters }
): Promise<SavedViewResponse> =>
  api
    .put<Response<SavedViewResponse>>(`/insights/saved-views/${id}`, params)
    .then((res) => res.data.payload);

export const deleteSavedView = (id: number): Promise<Response> =>
  api.delete<Response>(`/insights/saved-views/${id}`).then((res) => res.data);
